import {
  IonAlert,
  IonButton,
  IonCard,
  IonCardContent,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonNote,
  IonTextarea,
  IonToast,
  IonToggle,
} from "@ionic/react";
import React, { useEffect, useState } from "react";
import { useArtists } from "../data/store/useArtists";
import { useAnnouncements } from "../data/store/useAnnouncements";
import { useProfile } from "../data/store/useProfile";
import { Announcement } from "../models/Announcement";
import { renderInlineLinks } from "../util/renderInlineLinks";

const formatExpiry = (ms: number) => {
  const date = new Date(ms);
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const AnnouncementsAdmin: React.FC = () => {
  const { ownerId, authorId } = useProfile();
  const artist = useArtists((x) =>
    authorId ? x.retrieve(authorId) : undefined,
  );
  const all = useAnnouncements((s) => s.all);
  const create = useAnnouncements((s) => s.create);
  const remove = useAnnouncements((s) => s.remove);
  const observe = useAnnouncements((s) => s.observe);

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [dismissible, setDismissible] = useState(true);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const off = observe(ownerId);
    return off;
  }, [ownerId, observe]);

  const handleCreate = async () => {
    if (!ownerId || !body.trim() || submitting) return;
    setSubmitting(true);
    try {
      await create(
        ownerId,
        artist?.alias || "Admin",
        title.trim(),
        body.trim(),
        { dismissible },
      );
      setTitle("");
      setBody("");
      setDismissible(true);
    } catch (e) {
      console.error("Failed to post announcement:", e);
      setErrorMessage(
        `Couldn't publish the announcement: ${
          e instanceof Error ? e.message : String(e)
        }`,
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await remove(id);
    } catch (e) {
      console.error("Failed to delete announcement:", e);
      setErrorMessage(
        `Couldn't delete the announcement: ${
          e instanceof Error ? e.message : String(e)
        }`,
      );
    }
  };

  const now = Date.now();
  const sorted = [...all].sort((a, b) => b.createdAt - a.createdAt);

  return (
    <>
      <IonCard>
        <IonCardContent>
          <h2 style={{ marginTop: 0 }}>New announcement</h2>
          <IonList>
            <IonItem>
              <IonLabel position="stacked" color="primary">
                Title (optional)
              </IonLabel>
              <IonInput
                value={title}
                placeholder="e.g. Concert this Friday"
                onIonChange={(e) => setTitle(e.detail.value || "")}
                maxlength={80}
              />
            </IonItem>
            <IonItem>
              <IonLabel position="stacked" color="primary">
                Body
              </IonLabel>
              <IonTextarea
                value={body}
                placeholder="Type the announcement. Use [link text](https://example.com) to add hyperlinks."
                autoGrow
                rows={3}
                onIonChange={(e) => setBody(e.detail.value || "")}
              />
              <IonNote slot="helper">
                Hyperlinks: write{" "}
                <code>[Friday's concert](https://example.com)</code> and the
                bracket text becomes a clickable link in the banner.
              </IonNote>
            </IonItem>
            <IonItem lines="none">
              <IonLabel>Dismissible</IonLabel>
              <IonToggle
                checked={dismissible}
                onIonChange={(e) => setDismissible(e.detail.checked)}
                aria-label="Allow users to dismiss this announcement"
              />
            </IonItem>
          </IonList>

          {body.trim() && (
            <div
              style={{
                marginTop: 12,
                padding: 12,
                border: "1px dashed rgba(0,0,0,0.15)",
                borderRadius: 8,
                background: "rgba(21, 96, 189, 0.04)",
              }}
            >
              <small style={{ opacity: 0.6 }}>Live preview</small>
              {title.trim() && (
                <div style={{ fontWeight: 600, marginTop: 4 }}>{title}</div>
              )}
              <div style={{ marginTop: 4 }}>{renderInlineLinks(body)}</div>
            </div>
          )}

          <IonButton
            expand="block"
            color="primary"
            disabled={!body.trim() || submitting}
            onClick={handleCreate}
            style={{ marginTop: 16 }}
          >
            {submitting ? "Publishing…" : "Publish announcement"}
          </IonButton>
          <IonNote>
            Default expiry: 30 days from now. You can also delete an
            announcement manually below.
          </IonNote>
        </IonCardContent>
      </IonCard>

      <IonCard>
        <IonCardContent>
          <h2 style={{ marginTop: 0 }}>Current announcements</h2>
          {sorted.length === 0 && (
            <p style={{ opacity: 0.6 }}>None yet.</p>
          )}
          <IonList>
            {sorted.map((a: Announcement) => {
              const expired = a.expiresAt <= now;
              return (
                <IonItem key={a.id} lines="full">
                  <IonLabel className="ion-text-wrap">
                    {a.title && (
                      <h3 style={{ margin: 0 }}>
                        {a.title}
                        {expired && (
                          <span
                            style={{
                              marginLeft: 8,
                              fontSize: 12,
                              color: "var(--ion-color-medium)",
                            }}
                          >
                            (expired)
                          </span>
                        )}
                      </h3>
                    )}
                    <p style={{ marginTop: a.title ? 4 : 0 }}>
                      {renderInlineLinks(a.body)}
                      {!a.title && expired && (
                        <span
                          style={{
                            marginLeft: 8,
                            fontSize: 12,
                            color: "var(--ion-color-medium)",
                          }}
                        >
                          (expired)
                        </span>
                      )}
                    </p>
                    <small style={{ opacity: 0.6 }}>
                      Posted by {a.fromAlias} · Expires{" "}
                      {formatExpiry(a.expiresAt)}
                      {!a.dismissible && " · Not dismissible"}
                    </small>
                  </IonLabel>
                  <IonButton
                    slot="end"
                    fill="clear"
                    color="danger"
                    onClick={() => setConfirmDeleteId(a.id)}
                    aria-label={`Delete announcement: ${a.title}`}
                  >
                    Delete
                  </IonButton>
                </IonItem>
              );
            })}
          </IonList>
        </IonCardContent>
      </IonCard>

      <IonAlert
        isOpen={confirmDeleteId !== null}
        onDidDismiss={() => setConfirmDeleteId(null)}
        header="Delete announcement?"
        message="It will disappear for everyone immediately."
        buttons={[
          {
            text: "Cancel",
            role: "cancel",
          },
          {
            text: "Delete",
            role: "destructive",
            handler: () => {
              if (confirmDeleteId) {
                handleDelete(confirmDeleteId);
              }
              setConfirmDeleteId(null);
            },
          },
        ]}
      />

      <IonToast
        isOpen={errorMessage !== null}
        message={errorMessage || ""}
        duration={5000}
        color="danger"
        position="top"
        onDidDismiss={() => setErrorMessage(null)}
      />
    </>
  );
};

export default AnnouncementsAdmin;
